/**
 * One record, 360°.
 *
 * The ego graph shows what it is connected to; the lineage panel shows which
 * source row asserted each value and which survivorship rule decided it. That
 * second half is the point — a golden record that cannot explain itself is
 * just a guess with good formatting.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ErrorNotice, Loading, relativeTime } from '@/shared/components'
import { Chip, KeyValueGrid, Mark, SubTabs, Value, type ChipTone } from '../components'
import { EgoGraphView } from './EgoGraphView'
import { useEgo, useLineage, useRecords } from '../hooks'
import type { EgoNode, RecordRow } from '../types/brain.types'
import '../styles/brain.css'

type Panel = 'graph' | 'properties' | 'lineage'

const STATUS_TONE: Record<string, ChipTone> = {
  ACTIVE: 'ok',
  REVIEW: 'warn',
  FLAGGED: 'err',
  MERGED_AWAY: 'plain',
}

export function RecordDetailPage() {
  const { graphId = '', entityId = '' } = useParams<{ graphId: string; entityId: string }>()
  const navigate = useNavigate()
  const [panel, setPanel] = useState<Panel>('graph')
  // Which record the inspector is describing, and nothing until you click one.
  // The panel is a *detail* view: opening it by default answers a question
  // nobody asked yet and takes half the canvas to do it. Click a record to open
  // it, click the background or press Escape to close it again.
  const [selected, setSelected] = useState<string | null>(null)

  // Following a link re-mounts this page with a new id; the selection from the
  // record you came from would otherwise be pointing at nothing.
  useEffect(() => setSelected(null), [entityId])

  // Escape closes the detail panel, the same key that closes every other panel
  // in the app.
  useEffect(() => {
    if (!selected) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelected(null)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [selected])

  // One hop, always. The neighbourhood of a record is what this page is about;
  // records further out belong to their own page, and a depth dial would make
  // the picture a question the reader has to answer before they can read it.
  const ego = useEgo(graphId, entityId, 1)
  const lineage = useLineage(graphId, entityId)

  // The list query already holds the projected record; asking for it again by
  // id would be a second round trip for the same bytes.
  const listed = useRecords(graphId, {}).data?.records.find((r) => r.entity_id === entityId)
  const center = ego.data?.center

  /** The record the inspector describes — only ever one you clicked. */
  const shown: EgoNode | undefined = useMemo(() => {
    if (!selected) return undefined
    return (ego.data?.nodes ?? []).find((n) => n.entity_id === selected)
  }, [ego.data, selected])

  const isCentre = shown?.entity_id === entityId

  /** The relationships in play, so the header can name them rather than count. */
  const relations = useMemo(() => {
    const kinds = new Set((ego.data?.edges ?? []).map((e) => e.edge_type_key))
    return [...kinds].sort()
  }, [ego.data])

  /** Every edge touching the record on show, with the record at its far end. */
  const shownEdges = useMemo(() => {
    if (!shown || !ego.data) return []
    return ego.data.edges
      .filter((e) => e.from_entity === shown.entity_id || e.to_entity === shown.entity_id)
      .map((edge) => {
        const outgoing = edge.from_entity === shown.entity_id
        const otherId = outgoing ? edge.to_entity : edge.from_entity
        return {
          edge,
          outgoing,
          other: ego.data!.nodes.find((n) => n.entity_id === otherId),
        }
      })
  }, [shown, ego.data])

  if (ego.isLoading) return <Loading label="Loading the record…" />
  if (ego.error)
    return (
      <div className="page">
        <ErrorNotice error={ego.error} onRetry={ego.refetch} />
      </div>
    )

  return (
    <div className="cp-screen">
      <div className="cp-scroll">
      <button
        className="btn btn-ghost btn-sm"
        style={{ marginBottom: 10 }}
        onClick={() => navigate(`/brain/${graphId}?tab=records`)}
      >
        ← All records
      </button>

      <div className="cp-toolbar">
        <Mark name={center?.label ?? entityId} id={entityId} size={32} />
        <h1>{center?.label ?? entityId.slice(0, 8)}</h1>
        {center && <Chip tone="core">{center.node_type_key}</Chip>}
        {listed && <Chip tone={STATUS_TONE[listed.status] ?? 'plain'}>{listed.status}</Chip>}
        <span className="cp-spacer" />
        <code className="mono faint small">{entityId}</code>
        <div className="sub2">
          {ego.data?.nodes.length ?? 0} connected record
          {(ego.data?.nodes.length ?? 0) === 1 ? '' : 's'} · every value below can say which source
          asserted it and which rule decided it.
        </div>
      </div>

      <div className="record-metrics">
        <div className="record-metric"><span>Data quality</span><strong>{center?.quality_score == null ? 'Not scored' : `${Math.round(center.quality_score * 100)}%`}</strong><small>Current record quality</small></div>
        <div className="record-metric"><span>Related records</span><strong>{Math.max(0, (ego.data?.nodes.length ?? 1) - 1).toLocaleString()}</strong><small>Connected business context</small></div>
        <div className="record-metric"><span>Properties</span><strong>{Object.keys(listed?.properties ?? center?.properties ?? {}).length}</strong><small>Fields on this record</small></div>
        <div className="record-metric"><span>Contributing sources</span><strong>{lineage.data ? lineage.data.sources.length : '—'}</strong><small>Traceable source records</small></div>
      </div>

      <SubTabs<Panel>
        value={panel}
        onChange={setPanel}
        tabs={[
          { key: 'graph', label: 'Connections', count: ego.data?.edges.length },
          { key: 'properties', label: 'Properties' },
          { key: 'lineage', label: 'Lineage', count: lineage.data?.sources.length },
        ]}
      />

      <div style={{ marginTop: 16 }}>
        {panel === 'graph' && (
          <div className="stack">
            <div className="row">
              <span className="small muted">
                {(ego.data?.nodes.length ?? 1) - 1} related record
                {(ego.data?.nodes.length ?? 1) - 1 === 1 ? '' : 's'}
              </span>
              {/* Named rather than counted: "an account and two orders" is what
                  someone is looking at, and the colours on the canvas match. */}
              {relations.map((relation) => (
                <Chip key={relation} tone="plain">
                  {relation}
                </Chip>
              ))}
              {ego.data?.truncated && (
                <Chip tone="warn">too many to draw — showing the closest</Chip>
              )}
              <span className="cp-spacer" />
              <span className="small faint">
                {shown ? 'Esc closes the details' : 'Click a record to see its details'}
              </span>
            </div>

            {/* The picture and the facts side by side: the graph answers "what is
                this connected to", the panel answers "what does it say" — for
                whichever record on the canvas you last clicked. */}
            {/* One column until a record is clicked. The detail panel is not a
                sidebar that happens to be empty — it is closed, and the canvas
                has the width back. */}
            <div className="cp-split" data-open={true}>
              <div className="cp-canvaswrap">
                {ego.data && (
                  <EgoGraphView
                    ego={ego.data}
                    selectedId={shown?.entity_id ?? null}
                    onSelect={setSelected}
                  />
                )}
              </div>
              {!shown && (
                <aside className="cp-insp record-provenance">
                  <div className="cp-sect"><h3>Record provenance</h3><p className="small muted">Where the values in this record came from.</p></div>
                  {lineage.isLoading && <Loading label="Loading provenance..." />}
                  {lineage.error && <ErrorNotice error={lineage.error} onRetry={lineage.refetch} />}
                  {lineage.data && lineage.data.audit.filter((row) => row.field).slice(0, 5).map((row) => (
                    <div className="cp-sect" key={row.id}>
                      <span className="small muted">{row.field}</span>
                      <div className="provenance-value"><Value value={row.winning_value} /></div>
                      <span className="small provenance-source">{row.source_id ?? row.actor}{row.strategy ? ` · ${row.strategy.replace(/_/g, ' ')}` : ''}</span>
                    </div>
                  ))}
                  {lineage.data && !lineage.data.audit.some((row) => row.field) && <div className="cp-sect small muted">No field decisions recorded yet.</div>}
                  <div className="cp-sect"><button className="btn btn-block" onClick={() => setPanel('lineage')}>View full lineage</button></div>
                </aside>
              )}
              {shown && (
              <aside className="cp-insp">
                <>
                    <div className="cp-sect">
                      <div className="row-tight" style={{ alignItems: 'center' }}>
                        <Mark
                          name={shown.label ?? shown.entity_id}
                          id={shown.entity_id}
                          size={26}
                        />
                        <strong className="truncate">
                          {shown.label ?? shown.entity_id.slice(0, 8)}
                        </strong>
                      </div>
                      <div className="row-tight" style={{ marginTop: 6, flexWrap: 'wrap' }}>
                        <Chip tone="core">{shown.node_type_key}</Chip>
                        <Chip tone={STATUS_TONE[shown.status] ?? 'plain'}>{shown.status}</Chip>
                        <Chip tone="plain">
                          {isCentre ? 'this record' : 'related'}
                        </Chip>
                      </div>
                      {!isCentre && (
                        <button
                          className="btn btn-sm"
                          style={{ marginTop: 8 }}
                          onClick={() => navigate(`/brain/${graphId}/records/${shown.entity_id}`)}
                        >
                          Centre on this record →
                        </button>
                      )}
                    </div>

                    <div className="cp-sect">
                      <h4>Fields</h4>
                      {Object.entries(
                        isCentre ? listed?.properties ?? shown.properties : shown.properties,
                      ).length === 0 && (
                        <p className="small faint" style={{ margin: 0 }}>
                          This record has no values yet.
                        </p>
                      )}
                      {Object.entries(
                        isCentre ? listed?.properties ?? shown.properties : shown.properties,
                      ).map(([key, value]) => {
                        // Provenance is shown only where the audit recorded a
                        // decision, and only for the record this page is about —
                        // a neighbour's lineage is a page of its own.
                        const decided = isCentre
                          ? lineage.data?.audit.find((row) => row.field === key)
                          : undefined
                        return (
                          <div key={key} className="cp-prow">
                            <span className="k truncate">{key}</span>
                            <span style={{ marginLeft: 'auto', fontWeight: 640 }}>
                              <Value value={value} />
                            </span>
                            {decided && (
                              <span className="ty" style={{ marginLeft: 0 }}>
                                {decided.source_id ?? decided.actor}
                                {decided.strategy
                                  ? ` · ${decided.strategy.replace(/_/g, ' ')}`
                                  : ''}
                              </span>
                            )}
                          </div>
                        )
                      })}
                    </div>

                    <div className="cp-sect">
                      <h4>Connected to</h4>
                      {shownEdges.length === 0 && (
                        <p className="small faint" style={{ margin: 0 }}>
                          Nothing links to this record yet.
                        </p>
                      )}
                      {shownEdges.map(({ edge, outgoing, other }) => (
                        <button
                          key={edge.edge_id}
                          className="cp-erow cp-erow-btn"
                          onClick={() => other && setSelected(other.entity_id)}
                          title={other ? `Inspect ${other.label ?? other.node_type_key}` : undefined}
                        >
                          <span className="faint">{outgoing ? '→' : '←'}</span>
                          <span className="lab truncate">{edge.edge_type_key}</span>
                          <span className="to truncate">
                            {other?.label ?? other?.node_type_key ?? '—'}
                          </span>
                        </button>
                      ))}
                    </div>

                    <div className="cp-sect">
                      <h4>Quality</h4>
                      <div className="cp-erow">
                        <span className="lab">score</span>
                        <span className="to">
                          {shown.quality_score === null
                            ? 'not scored'
                            : `${Math.round(shown.quality_score * 100)}%`}
                        </span>
                      </div>
                      {isCentre && listed && (
                        <div className="cp-erow">
                          <span className="lab">contributing sources</span>
                          <span className="to">{listed.sources?.length ?? 0}</span>
                        </div>
                      )}
                      <div className="cp-erow">
                        <span className="lab">id</span>
                        <span className="to mono small truncate">{shown.entity_id}</span>
                      </div>
                    </div>
                </>
              </aside>
              )}
            </div>
          </div>
        )}

        {panel === 'properties' && (
          <PropertiesView record={listed} center={ego.data?.center.properties} />
        )}

        {panel === 'lineage' && (
          <div className="stack">
            <h3>Where the values came from</h3>
            {lineage.isLoading && <Loading />}
            {lineage.data?.sources.length === 0 && (
              <p className="muted small">
                Nothing recorded — this record was created directly rather than by the pipeline.
              </p>
            )}
            {(lineage.data?.sources.length ?? 0) > 0 && (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Their record id</th>
                      <th>Contributed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineage.data!.sources.map((source) => (
                      <tr key={source.extracted_record_id}>
                        <td className="mono">{source.source_id ?? '—'}</td>
                        <td className="mono small">{source.source_record_id ?? '—'}</td>
                        <td className="muted small">{relativeTime(source.at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h3 style={{ marginTop: 8 }}>Which value won, and why</h3>
            {lineage.data?.audit.length === 0 && (
              <p className="muted small">
                No field has had two competing values yet, so nothing has needed deciding.
              </p>
            )}
            {(lineage.data?.audit.length ?? 0) > 0 && (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>What happened</th>
                      <th>Field</th>
                      <th>Kept</th>
                      <th>Discarded</th>
                      <th>Because</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineage.data!.audit.map((row) => (
                      <tr key={row.id}>
                        <td className="muted small">{relativeTime(row.at)}</td>
                        <td>
                          <span className="badge badge-neutral">{row.action}</span>
                        </td>
                        <td className="mono small">{row.field ?? '—'}</td>
                        <td>
                          <Value value={row.winning_value} />
                        </td>
                        <td className="muted">
                          <Value value={row.losing_value} />
                        </td>
                        <td className="small muted">
                          {row.strategy?.replace(/_/g, ' ') ?? row.actor}
                          {row.detail ? ` — ${row.detail}` : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  )
}

function PropertiesView({
  record,
  center,
}: {
  record: RecordRow | undefined
  center: Record<string, unknown> | undefined
}) {
  const properties = record?.properties ?? center ?? {}
  const rows = Object.entries(properties).map(
    ([key, value]) => [key, <Value key={key} value={value} />] as [string, JSX.Element],
  )

  if (rows.length === 0) return <p className="muted small">This record has no values yet.</p>

  return (
    <div className="stack">
      <KeyValueGrid rows={rows} />
      <p className="small faint" style={{ margin: 0 }}>
        Values on properties flagged as personal data are masked here. The full value stays in the
        record and is never sent to the browser.
      </p>
    </div>
  )
}
